import { useState, useEffect, useMemo, useRef } from 'react'
import {
    FloppyDiskIcon,
    ArrowCounterClockwiseIcon,
    PlusIcon,
    TrashIcon,
} from '@phosphor-icons/react'
import {
    ScoopAccordion,
    ScoopButton,
    ScoopTextField,
    ScoopSelector,
    ScoopText,
    ScoopSwitch,
    ScoopTab,
    ScoopTabPanel,
    ScoopSpinner,
    ScoopDatePicker,
    ScoopRow,
    ScoopColumn,
    ScoopCenter,
    ScoopDialog,
} from '../../../../../ui'
import { useWorkspaceMetadata } from '../../../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { FormField, DefinitionTable } from '../shared'
import {
    parseContextToForm,
    buildContextFromForm,
    VALID_CLASSIFICATIONS,
    EMPTY_NL_PROBE,
    EMPTY_ZO_PROBE,
    EMPTY_ML_PROBE,
    EMPTY_PATTERN,
    EMPTY_INTERPRETATION,
    EMPTY_SPAWN,
    EMPTY_TERMINATION,
} from './contextFormModel'
import styles from './ContextForm.module.scss'

// Empty row templates live in contextFormModel (audit F11: valid
// classification defaults; merge-on-save preserves unmodeled fields).
const EMPTY_DATASET_CATEGORY = {
    category: '',
    datasets: [],
}

// Chat.java ROUTING values (SCHEMA.md v2.9.21) — the only classifications the
// engine can route. The old free-text field let invalid values through (F11).
const CLASSIFICATION_OPTIONS = [
    { label: 'Engine default (DATASET)', value: '' },
    ...VALID_CLASSIFICATIONS.map((value) => ({ label: value, value })),
]

const COMPARISON_BASELINE_OPTIONS = [
    { label: 'Same Month Last Year', value: 'same_month_last_year' },
    { label: 'Previous Month', value: 'previous_month' },
    { label: 'Previous Quarter', value: 'previous_quarter' },
    { label: 'Previous Year', value: 'previous_year' },
]

const ACTION_OPTIONS = [
    { label: 'Investigate', value: 'investigate' },
    { label: 'Complete', value: 'complete' },
    { label: 'Alert', value: 'alert' },
]

const STRATEGY_OPTIONS = [
    { label: 'Worst First', value: 'worst_first' },
    { label: 'All', value: 'all' },
    { label: 'Top N', value: 'top_n' },
]


const SPAWN_MODE_OPTIONS = [
    { label: 'Investigate', value: 'investigate' },
    { label: 'Diagnose', value: 'diagnose' },
    { label: 'Monitor', value: 'monitor' },
]

const TERMINATION_ACTION_OPTIONS = [
    { label: 'Complete with Success', value: 'complete_with_success' },
    { label: 'Complete with Warning', value: 'complete_with_warning' },
    { label: 'Complete with Error', value: 'complete_with_error' },
    { label: 'Escalate', value: 'escalate' },
]

const SEVERITY_OPTIONS = [
    { label: 'Info', value: 'info' },
    { label: 'Warning', value: 'warning' },
    { label: 'Error', value: 'error' },
    { label: 'Critical', value: 'critical' },
]

const OUTPUT_TYPE_OPTIONS = [
    { label: 'Table', value: 'table' },
    { label: 'Chart', value: 'chart' },
    { label: 'Scalar', value: 'scalar' },
    { label: 'Text', value: 'text' },
]

// Main tab definitions. "Validation Results" not "Validation" (review D26):
// this tab edits the validation_results config prose — it does not validate
// the form, and the shorter label read that way to operators.
const MAIN_TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'validation', label: 'Validation Results' },
    { key: 'definitions', label: 'Definitions' },
    { key: 'probes', label: 'Probes' },
    { key: 'patterns', label: 'Patterns' },
    { key: 'behavior', label: 'Behavior' },
    { key: 'business', label: 'Business' },
]

// Which formData sections belong to each tab — the unit of per-tab dirty
// tracking (design D26: the change-set must be visible).
const TAB_SECTIONS = {
    overview: ['name', 'metadata', 'applicable_datasets', 'screening_rules', 'client_feedback'],
    validation: ['validation_results'],
    definitions: ['local_definitions'],
    probes: ['nl_probes', 'zo_probes', 'ml_probes'],
    patterns: ['patterns', 'interpretation_guidance'],
    behavior: ['spawn_enabled', 'spawn_guidance', 'termination_conditions', 'synthesis_options'],
    business: ['business_context'],
}

/**
 * ContextForm - Form for editing context JSON files
 *
 * @param {Object} data - Current context content
 * @param {string} contextName - Name of the context being edited
 * @param {Function} onSave - Callback when saving
 * @param {boolean} isSaving - Whether save is in progress
 * @param {boolean} isLoading - Whether data is loading
 */
export const ContextForm = ({
    data,
    contextName,
    onSave,
    isSaving,
    isLoading,
    onDirtyStateChange,
}) => {
    const { workspaceMetadata } = useWorkspaceMetadata()
    const datasetOptions = (workspaceMetadata?.inboxes || []).map((inbox) => ({
        label: inbox.label || inbox.inboxName,
        value: inbox.label || inbox.inboxName,
    }))

    const [formData, setFormData] = useState({
        name: '',
        metadata: {
            display_name: '',
            purpose: '',
            description: '',
            key_metrics: '',
            usage: '',
            multi_dataset_note: '',
            last_validated: '',
        },
        applicable_datasets: [], // Array of {category, datasets}
        screening_rules: {
            primary_indicator: '',
            severity_filter: '',
            critical_definition: '',
            sort_order: '',
        },
        client_feedback: {
            source: '',
            summary: '',
            recommended_stores: '',
            reasoning: '',
        },
        validation_results: {
            previous_approach: '',
            corrected_approach: '',
        },
        local_definitions: [],
        nl_probes: [],
        zo_probes: [],
        ml_probes: [],
        patterns: [],
        interpretation_guidance: [],
        spawn_guidance: [],
        termination_conditions: [],
        business_context: {
            methodology: '',
            owner: '',
            frequency: '',
            escalation: '',
            kpi_targets: {
                goal: '',
                acceptable: '',
                action_required: '',
            },
            fiscal_calendar: {
                Q1: '',
                Q2: '',
                Q3: '',
                Q4: '',
                note: '',
            },
        },
        spawn_enabled: true,
        synthesis_options: {
            ml_enabled: undefined,
            ml_query: '',
            ml_classification: '',
            ml_threshold: '',
            ml_timeout: '',
            max_queries: '',
            narrative_voice: '',
            internal_guidance: '',
        },
    })
    const [activeTab, setActiveTab] = useState('overview')
    const [activeProbeTab, setActiveProbeTab] = useState('nl')
    const [hasChanges, setHasChanges] = useState(false)
    const [showResetConfirm, setShowResetConfirm] = useState(false)
    const [jsonErrors, setJsonErrors] = useState({})
    const initialFormDataRef = useRef(null)

    useEffect(() => {
        onDirtyStateChange?.(hasChanges)
    }, [hasChanges, onDirtyStateChange])

    // Initialize form data (parse lives in contextFormModel — audit W2.1)
    useEffect(() => {
        if (data) {
            const parsed = parseContextToForm(data, contextName)
            setFormData(parsed)
            initialFormDataRef.current = parsed
            setHasChanges(false)
            setJsonErrors({})
        }
    }, [data, contextName])

    // Per-tab dirty tracking (design D26): compare each tab's sections to the
    // last-loaded state; the tab label carries the marker.
    const dirtyTabs = useMemo(() => {
        const initial = initialFormDataRef.current
        if (!initial || !hasChanges) return new Set()
        const dirty = new Set()
        for (const [tabKey, sections] of Object.entries(TAB_SECTIONS)) {
            if (
                sections.some(
                    (key) => JSON.stringify(formData[key]) !== JSON.stringify(initial[key])
                )
            ) {
                dirty.add(tabKey)
            }
        }
        return dirty
    }, [formData, hasChanges])

    const tabsWithDirty = useMemo(
        () =>
            MAIN_TABS.map((tab) => ({
                ...tab,
                label: dirtyTabs.has(tab.key) ? `${tab.label} •` : tab.label,
            })),
        [dirtyTabs]
    )

    // =========================================================================
    // HANDLERS
    // =========================================================================

    const handleMetadataChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            metadata: { ...prev.metadata, [field]: value },
        }))
        setHasChanges(true)
    }

    const handleScreeningRulesChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            screening_rules: { ...prev.screening_rules, [field]: value },
        }))
        setHasChanges(true)
    }

    const handleClientFeedbackChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            client_feedback: { ...prev.client_feedback, [field]: value },
        }))
        setHasChanges(true)
    }

    const handleValidationResultsChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            validation_results: { ...prev.validation_results, [field]: value },
        }))
        setHasChanges(true)
    }

    const handleApplicableDatasetsChange = (index, field, value) => {
        setFormData((prev) => {
            const updated = [...prev.applicable_datasets]
            updated[index] = { ...updated[index], [field]: value }
            return { ...prev, applicable_datasets: updated }
        })
        setHasChanges(true)
    }

    const handleAddApplicableDataset = () => {
        setFormData((prev) => ({
            ...prev,
            applicable_datasets: [...prev.applicable_datasets, { ...EMPTY_DATASET_CATEGORY }],
        }))
        setHasChanges(true)
    }

    const handleRemoveApplicableDataset = (index) => {
        setFormData((prev) => ({
            ...prev,
            applicable_datasets: prev.applicable_datasets.filter((_, i) => i !== index),
        }))
        setHasChanges(true)
    }

    const handleLocalDefinitionsChange = (newDefs) => {
        setFormData((prev) => ({ ...prev, local_definitions: newDefs }))
        setHasChanges(true)
    }

    // Probe handlers
    const handleProbeChange = (probeType, index, field, value) => {
        setFormData((prev) => {
            const key = `${probeType}_probes`
            const updated = [...prev[key]]
            updated[index] = { ...updated[index], [field]: value }
            return { ...prev, [key]: updated }
        })
        setHasChanges(true)
    }

    const handleAddProbe = (probeType) => {
        const makeEmptyProbe =
            probeType === 'nl'
                ? EMPTY_NL_PROBE
                : probeType === 'zo'
                  ? EMPTY_ZO_PROBE
                  : EMPTY_ML_PROBE
        setFormData((prev) => ({
            ...prev,
            [`${probeType}_probes`]: [...prev[`${probeType}_probes`], makeEmptyProbe()],
        }))
        setHasChanges(true)
    }

    const handleRemoveProbe = (probeType, index) => {
        setFormData((prev) => ({
            ...prev,
            [`${probeType}_probes`]: prev[`${probeType}_probes`].filter((_, i) => i !== index),
        }))
        setHasChanges(true)
    }

    // Pattern handlers
    const handlePatternChange = (index, field, value) => {
        setFormData((prev) => {
            const updated = [...prev.patterns]
            updated[index] = { ...updated[index], [field]: value }
            return { ...prev, patterns: updated }
        })
        setHasChanges(true)
    }

    const handleAddPattern = () => {
        setFormData((prev) => ({
            ...prev,
            patterns: [...prev.patterns, EMPTY_PATTERN()],
        }))
        setHasChanges(true)
    }

    const handleRemovePattern = (index) => {
        setFormData((prev) => ({
            ...prev,
            patterns: prev.patterns.filter((_, i) => i !== index),
        }))
        setHasChanges(true)
    }

    // Interpretation guidance handlers
    const handleInterpretationChange = (index, field, value) => {
        setFormData((prev) => {
            const updated = [...prev.interpretation_guidance]
            updated[index] = { ...updated[index], [field]: value }
            return { ...prev, interpretation_guidance: updated }
        })
        setHasChanges(true)
    }

    const handleAddInterpretation = () => {
        setFormData((prev) => ({
            ...prev,
            interpretation_guidance: [...prev.interpretation_guidance, EMPTY_INTERPRETATION()],
        }))
        setHasChanges(true)
    }

    const handleRemoveInterpretation = (index) => {
        setFormData((prev) => ({
            ...prev,
            interpretation_guidance: prev.interpretation_guidance.filter((_, i) => i !== index),
        }))
        setHasChanges(true)
    }

    // Spawn handlers
    const handleSpawnChange = (index, field, value) => {
        setFormData((prev) => {
            const updated = [...prev.spawn_guidance]
            updated[index] = { ...updated[index], [field]: value }
            return { ...prev, spawn_guidance: updated }
        })
        setHasChanges(true)
    }

    const handleAddSpawn = () => {
        setFormData((prev) => ({
            ...prev,
            spawn_guidance: [...prev.spawn_guidance, EMPTY_SPAWN()],
        }))
        setHasChanges(true)
    }

    const handleRemoveSpawn = (index) => {
        setFormData((prev) => ({
            ...prev,
            spawn_guidance: prev.spawn_guidance.filter((_, i) => i !== index),
        }))
        setHasChanges(true)
    }

    // Termination condition handlers
    const handleTerminationChange = (index, field, value) => {
        setFormData((prev) => {
            const updated = [...prev.termination_conditions]
            updated[index] = { ...updated[index], [field]: value }
            return { ...prev, termination_conditions: updated }
        })
        setHasChanges(true)
    }

    const handleAddTermination = () => {
        setFormData((prev) => ({
            ...prev,
            termination_conditions: [...prev.termination_conditions, EMPTY_TERMINATION()],
        }))
        setHasChanges(true)
    }

    const handleRemoveTermination = (index) => {
        setFormData((prev) => ({
            ...prev,
            termination_conditions: prev.termination_conditions.filter((_, i) => i !== index),
        }))
        setHasChanges(true)
    }

    // Business context handlers
    const handleBusinessContextChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            business_context: { ...prev.business_context, [field]: value },
        }))
        setHasChanges(true)
    }

    const handleKpiTargetsChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            business_context: {
                ...prev.business_context,
                kpi_targets: { ...prev.business_context.kpi_targets, [field]: value },
            },
        }))
        setHasChanges(true)
    }

    const handleFiscalCalendarChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            business_context: {
                ...prev.business_context,
                fiscal_calendar: { ...prev.business_context.fiscal_calendar, [field]: value },
            },
        }))
        setHasChanges(true)
    }

    // Synthesis handlers (investigation.synthesis_options — the real key, audit F12)
    const handleSynthesisChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            synthesis_options: { ...prev.synthesis_options, [field]: value },
        }))
        setHasChanges(true)
    }

    // =========================================================================
    // SAVE HANDLER
    // =========================================================================

    const handleSave = () => {
        // Merge-on-save (contextFormModel, audit W2.1): only fields the operator
        // actually edited are written; everything else — query_json,
        // report_detail, annotations, unknown keys — is preserved verbatim.
        onSave(buildContextFromForm(data, formData, contextName))
        setHasChanges(false)
    }

    const handleReset = () => {
        if (data) {
            const parsed = parseContextToForm(data, contextName)
            setFormData(parsed)
            initialFormDataRef.current = parsed
            setHasChanges(false)
            setJsonErrors({})
        }
        setShowResetConfirm(false)
    }

    // Validate JSON textareas on blur into FormField's error slot (design D26)
    // — today a typo saves silently and the engine eats the failure.
    const handleJsonBlur = (key, value) => {
        const trimmed = (value || '').trim()
        if (!trimmed) {
            setJsonErrors((prev) => ({ ...prev, [key]: null }))
            return
        }
        try {
            JSON.parse(trimmed)
            setJsonErrors((prev) => ({ ...prev, [key]: null }))
        } catch (error) {
            setJsonErrors((prev) => ({ ...prev, [key]: `Invalid JSON: ${error.message}` }))
        }
    }

    // =========================================================================
    // RENDER HELPERS - TAB CONTENT
    // =========================================================================

    const renderOverviewTab = () => (
        <div className={styles.tabContent}>
            {/* Context Metadata */}
            <div className={styles.section}>
                <ScoopText className={styles.sectionTitle}>Context Metadata</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    Basic information about this investigation context.
                </ScoopText>
                <ScoopColumn className={styles.fields}>
                    <ScoopRow className={styles.fieldRow}>
                        <FormField
                            label="Context Name"
                            helpText="Technical identifier (read-only for existing contexts)"
                        >
                            <ScoopTextField
                                value={formData.name}
                                disabled
                                placeholder="context_name"
                            />
                        </FormField>
                        <FormField
                            label="Display Name"
                            helpText="User-friendly name shown in the UI"
                        >
                            <ScoopTextField
                                value={formData.metadata.display_name}
                                onChange={(e) =>
                                    handleMetadataChange('display_name', e.target.value)
                                }
                                placeholder="My Context"
                                disabled={isSaving}
                            />
                        </FormField>
                    </ScoopRow>
                    <FormField label="Purpose" helpText="What this context investigates">
                        <ScoopTextField
                            value={formData.metadata.purpose}
                            onChange={(e) => handleMetadataChange('purpose', e.target.value)}
                            placeholder="Describe the purpose of this investigation context"
                            multiline
                            rows={2}
                            disabled={isSaving}
                        />
                    </FormField>
                    <FormField label="Description" helpText="Detailed description of this context">
                        <ScoopTextField
                            value={formData.metadata.description}
                            onChange={(e) => handleMetadataChange('description', e.target.value)}
                            placeholder="Provide a detailed description..."
                            multiline
                            rows={3}
                            disabled={isSaving}
                        />
                    </FormField>
                    <FormField label="Usage" helpText="How to use this context">
                        <ScoopTextField
                            value={formData.metadata.usage}
                            onChange={(e) => handleMetadataChange('usage', e.target.value)}
                            placeholder="Run as screening context to identify..."
                            multiline
                            rows={2}
                            disabled={isSaving}
                        />
                    </FormField>
                    <ScoopRow className={styles.fieldRow}>
                        <FormField
                            label="Key Metrics"
                            helpText="Primary metrics examined (comma-separated)"
                        >
                            <ScoopTextField
                                value={formData.metadata.key_metrics}
                                onChange={(e) =>
                                    handleMetadataChange('key_metrics', e.target.value)
                                }
                                placeholder="metric1, metric2, metric3"
                                disabled={isSaving}
                            />
                        </FormField>
                        <FormField
                            label="Last Validated"
                            helpText="Date this context was last validated"
                        >
                            <ScoopDatePicker
                                value={
                                    formData.metadata.last_validated
                                        ? new Date(formData.metadata.last_validated + 'T00:00:00')
                                        : null
                                }
                                onChange={(date) => {
                                    const yyyy = date.getFullYear()
                                    const mm = String(date.getMonth() + 1).padStart(2, '0')
                                    const dd = String(date.getDate()).padStart(2, '0')
                                    handleMetadataChange('last_validated', `${yyyy}-${mm}-${dd}`)
                                }}
                                instant
                                isGuestMode={isSaving}
                            />
                        </FormField>
                    </ScoopRow>
                    <FormField
                        label="Multi-Dataset Note"
                        helpText="Important notes about dataset requirements"
                    >
                        <ScoopTextField
                            value={formData.metadata.multi_dataset_note}
                            onChange={(e) =>
                                handleMetadataChange('multi_dataset_note', e.target.value)
                            }
                            placeholder="CRITICAL: This analysis requires TWO datasets..."
                            multiline
                            rows={2}
                            disabled={isSaving}
                        />
                    </FormField>
                </ScoopColumn>
            </div>

            {/* Applicable Datasets */}
            <div className={styles.section}>
                <ScoopText className={styles.sectionTitle}>Applicable Datasets</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    Datasets this context uses, organized by category.
                </ScoopText>
                <div className={styles.itemList}>
                    {formData.applicable_datasets.length === 0 ? (
                        <ScoopText className={styles.emptyMessage}>
                            No applicable datasets defined.
                        </ScoopText>
                    ) : (
                        formData.applicable_datasets.map((ad, index) => (
                            <div key={index} className={styles.itemCard}>
                                <ScoopRow className={styles.itemHeader}>
                                    <FormField label="Category">
                                        <ScoopTextField
                                            value={ad.category}
                                            onChange={(e) =>
                                                handleApplicableDatasetsChange(
                                                    index,
                                                    'category',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="plo_and_revenue"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <ScoopButton
                                        variant="grey"
                                        size="xs"
                                        onClick={() => handleRemoveApplicableDataset(index)}
                                        disabled={isSaving}
                                    >
                                        <TrashIcon size={16} />
                                    </ScoopButton>
                                </ScoopRow>
                                <FormField
                                    label="Datasets"
                                    helpText="Select the datasets this context uses"
                                >
                                    <ScoopSelector
                                        value={ad.datasets}
                                        onChange={(e) =>
                                            handleApplicableDatasetsChange(
                                                index,
                                                'datasets',
                                                e.target.value
                                            )
                                        }
                                        options={datasetOptions}
                                        multiple
                                        searchable
                                        placeholder="Select datasets..."
                                        disabled={isSaving}
                                        showChips
                                        chipsDeletable
                                    />
                                </FormField>
                            </div>
                        ))
                    )}
                    <ScoopButton
                        variant="grey"
                        size="sm"
                        onClick={handleAddApplicableDataset}
                        disabled={isSaving}
                        leftSection={<PlusIcon size={16} />}
                    >
                        Add Dataset Category
                    </ScoopButton>
                </div>
            </div>
        </div>
    )

    const renderValidationTab = () => (
        <div className={styles.tabContent}>
            {/* Screening Rules */}
            <div className={styles.section}>
                <ScoopText className={styles.sectionTitle}>Screening Rules</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    Rules for screening and filtering results.
                </ScoopText>
                <ScoopColumn className={styles.fields}>
                    <FormField label="Primary Indicator" helpText="Main metric used for screening">
                        <ScoopTextField
                            value={formData.screening_rules.primary_indicator}
                            onChange={(e) =>
                                handleScreeningRulesChange('primary_indicator', e.target.value)
                            }
                            placeholder="PLO-Inventory Spread (Inventory YoY Growth - PLO YoY Growth)"
                            disabled={isSaving}
                        />
                    </FormField>
                    <FormField label="Severity Filter" helpText="Filter condition for severity">
                        <ScoopTextField
                            value={formData.screening_rules.severity_filter}
                            onChange={(e) =>
                                handleScreeningRulesChange('severity_filter', e.target.value)
                            }
                            placeholder="Net Revenue YoY Growth < 0%"
                            disabled={isSaving}
                        />
                    </FormField>
                    <FormField label="Critical Definition" helpText="What defines a critical case">
                        <ScoopTextField
                            value={formData.screening_rules.critical_definition}
                            onChange={(e) =>
                                handleScreeningRulesChange('critical_definition', e.target.value)
                            }
                            placeholder="Large positive spread combined with declining revenue"
                            multiline
                            rows={2}
                            disabled={isSaving}
                        />
                    </FormField>
                    <FormField label="Sort Order" helpText="How to sort results">
                        <ScoopTextField
                            value={formData.screening_rules.sort_order}
                            onChange={(e) =>
                                handleScreeningRulesChange('sort_order', e.target.value)
                            }
                            placeholder="Spread descending (largest imbalance first)"
                            disabled={isSaving}
                        />
                    </FormField>
                </ScoopColumn>
            </div>

            {/* Client Feedback */}
            <div className={styles.section}>
                <ScoopText className={styles.sectionTitle}>Client Feedback</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    Feedback and recommendations from clients.
                </ScoopText>
                <ScoopColumn className={styles.fields}>
                    <ScoopRow className={styles.fieldRow}>
                        <FormField label="Source" helpText="Who provided the feedback">
                            <ScoopTextField
                                value={formData.client_feedback.source}
                                onChange={(e) =>
                                    handleClientFeedbackChange('source', e.target.value)
                                }
                                placeholder="Nikki Swies, Jan 26 2026"
                                disabled={isSaving}
                            />
                        </FormField>
                        <FormField
                            label="Recommended Entities"
                            helpText="Comma-separated entity IDs"
                        >
                            <ScoopTextField
                                value={formData.client_feedback.recommended_stores}
                                onChange={(e) =>
                                    handleClientFeedbackChange('recommended_stores', e.target.value)
                                }
                                placeholder="87146, 87111, 87103"
                                disabled={isSaving}
                            />
                        </FormField>
                    </ScoopRow>
                    <FormField label="Summary" helpText="Summary of the feedback">
                        <ScoopTextField
                            value={formData.client_feedback.summary}
                            onChange={(e) => handleClientFeedbackChange('summary', e.target.value)}
                            placeholder="Original approach flagged the wrong entities..."
                            multiline
                            rows={2}
                            disabled={isSaving}
                        />
                    </FormField>
                    <FormField label="Reasoning" helpText="Reasoning behind recommendations">
                        <ScoopTextField
                            value={formData.client_feedback.reasoning}
                            onChange={(e) =>
                                handleClientFeedbackChange('reasoning', e.target.value)
                            }
                            placeholder="Spread is strongest mathematical indicator..."
                            multiline
                            rows={2}
                            disabled={isSaving}
                        />
                    </FormField>
                </ScoopColumn>
            </div>

            {/* Validation Results */}
            <div className={styles.section}>
                <ScoopText className={styles.sectionTitle}>Validation Results</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    Results from validating the context against real data.
                </ScoopText>
                <ScoopColumn className={styles.fields}>
                    <FormField
                        label="Previous Approach"
                        helpText="JSON or description of previous approach results"
                    >
                        <ScoopTextField
                            value={formData.validation_results.previous_approach}
                            onChange={(e) =>
                                handleValidationResultsChange('previous_approach', e.target.value)
                            }
                            placeholder='{"entities_found": ["87170"], "issue": "..."}'
                            multiline
                            rows={4}
                            className={styles.codeInput}
                            disabled={isSaving}
                        />
                    </FormField>
                    <FormField
                        label="Corrected Approach"
                        helpText="JSON or description of corrected approach results"
                    >
                        <ScoopTextField
                            value={formData.validation_results.corrected_approach}
                            onChange={(e) =>
                                handleValidationResultsChange('corrected_approach', e.target.value)
                            }
                            placeholder='{"entities_found": ["87146"], "december_2025_values": {...}}'
                            multiline
                            rows={4}
                            className={styles.codeInput}
                            disabled={isSaving}
                        />
                    </FormField>
                </ScoopColumn>
            </div>
        </div>
    )

    const renderDefinitionsTab = () => (
        <div className={styles.tabContentFill}>
            <div className={styles.tableSection}>
                <ScoopText className={styles.sectionTitle}>Local Definitions</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    Define terms specific to this investigation context. These override global
                    definitions if names match.
                </ScoopText>
                <DefinitionTable
                    definitions={formData.local_definitions}
                    onChange={handleLocalDefinitionsChange}
                    disabled={isSaving}
                    emptyMessage="No local definitions. Add terms specific to this context."
                    showAdvancedColumns
                />
            </div>
        </div>
    )

    const renderProbesTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.section}>
                <ScoopText className={styles.sectionTitle}>Probes</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    Configure the queries and analyses this context performs.
                </ScoopText>

                <ScoopTab
                    tabs={[
                        {
                            key: 'nl',
                            label: 'Natural Language',
                            badgeCount: formData.nl_probes.length,
                        },
                        {
                            key: 'zo',
                            label: 'Zero Overhead Drills',
                            badgeCount: formData.zo_probes.length,
                        },
                        {
                            key: 'ml',
                            label: 'ML Comparisons',
                            badgeCount: formData.ml_probes.length,
                        },
                    ]}
                    value={activeProbeTab}
                    onChange={(e, newValue) => setActiveProbeTab(newValue)}
                    variant="underline"
                    fullWidth={false}
                />

                <ScoopTabPanel value="nl" selectedValue={activeProbeTab}>
                    <div className={styles.itemList}>
                        {formData.nl_probes.length === 0 ? (
                            <ScoopText className={styles.emptyMessage}>
                                No natural language probes defined.
                            </ScoopText>
                        ) : (
                            formData.nl_probes.map((probe, index) => (
                                <ScoopAccordion
                                    key={index}
                                    defaultExpanded={index === 0}
                                    className={styles.itemAccordion}
                                >
                                    <ScoopAccordion.Summary>
                                        <div className={styles.itemSummary}>
                                            <ScoopText className={styles.itemSummaryTitle}>
                                                {probe.name || probe.probe_id || `Probe ${index + 1}`}
                                            </ScoopText>
                                            {probe.classification ? (
                                                <ScoopText className={styles.itemSummaryMeta}>
                                                    {probe.classification}
                                                </ScoopText>
                                            ) : null}
                                        </div>
                                    </ScoopAccordion.Summary>
                                    <ScoopAccordion.Details>
                                        <div className={styles.itemCard}>
                                    <ScoopRow className={styles.itemHeader}>
                                        <FormField label="Probe ID">
                                            <ScoopTextField
                                                value={probe.probe_id}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'nl',
                                                        index,
                                                        'probe_id',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="probe_id"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                        <ScoopButton
                                            variant="grey"
                                            size="xs"
                                            onClick={() => handleRemoveProbe('nl', index)}
                                            disabled={isSaving}
                                        >
                                            <TrashIcon size={16} />
                                        </ScoopButton>
                                    </ScoopRow>
                                    <ScoopRow className={styles.fieldRow}>
                                        <FormField label="Name" helpText="Human-readable name">
                                            <ScoopTextField
                                                value={probe.name}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'nl',
                                                        index,
                                                        'name',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Imbalanced Entity Screening"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                        <FormField
                                            label="Classification"
                                            helpText="Engine routing (not execution type)"
                                        >
                                            <ScoopSelector
                                                value={probe.classification}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'nl',
                                                        index,
                                                        'classification',
                                                        e.target.value
                                                    )
                                                }
                                                options={CLASSIFICATION_OPTIONS}
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                    </ScoopRow>
                                    <FormField label="Prompt">
                                        <ScoopTextField
                                            value={probe.prompt}
                                            onChange={(e) =>
                                                handleProbeChange(
                                                    'nl',
                                                    index,
                                                    'prompt',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Natural language query for AI"
                                            multiline
                                            rows={3}
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <FormField label="Purpose">
                                        <ScoopTextField
                                            value={probe.purpose}
                                            onChange={(e) =>
                                                handleProbeChange(
                                                    'nl',
                                                    index,
                                                    'purpose',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Why this probe matters"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <ScoopRow className={styles.fieldRow}>
                                        <FormField
                                            label="Output Columns"
                                            helpText="Comma-separated"
                                        >
                                            <ScoopTextField
                                                value={probe.output_columns}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'nl',
                                                        index,
                                                        'output_columns',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Entity ID, PLO YoY %, Inventory YoY %"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                        <FormField label="Output Type">
                                            <ScoopSelector
                                                value={probe.expected_output_type}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'nl',
                                                        index,
                                                        'expected_output_type',
                                                        e.target.value
                                                    )
                                                }
                                                options={OUTPUT_TYPE_OPTIONS}
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                    </ScoopRow>
                                    <FormField
                                        label="Dataset Requirements"
                                        helpText="JSON mapping metrics to datasets"
                                        error={jsonErrors[`nl-${index}`]}
                                    >
                                        <ScoopTextField
                                            value={probe.dataset_requirements}
                                            onChange={(e) =>
                                                handleProbeChange(
                                                    'nl',
                                                    index,
                                                    'dataset_requirements',
                                                    e.target.value
                                                )
                                            }
                                            onBlur={(e) =>
                                                handleJsonBlur(`nl-${index}`, e.target.value)
                                            }
                                            placeholder='{"Metric YoY Growth": {"dataset": "I12345", "column": "Source Column Name"}}'
                                            multiline
                                            rows={3}
                                            className={styles.codeInput}
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                        </div>
                                    </ScoopAccordion.Details>
                                </ScoopAccordion>
                            ))
                        )}
                        <ScoopButton
                            variant="grey"
                            size="sm"
                            onClick={() => handleAddProbe('nl')}
                            disabled={isSaving}
                            leftSection={<PlusIcon size={16} />}
                        >
                            Add Natural Language Probe
                        </ScoopButton>
                    </div>
                </ScoopTabPanel>

                <ScoopTabPanel value="zo" selectedValue={activeProbeTab}>
                    <div className={styles.itemList}>
                        {formData.zo_probes.length === 0 ? (
                            <ScoopText className={styles.emptyMessage}>
                                No zero overhead drill probes defined.
                            </ScoopText>
                        ) : (
                            formData.zo_probes.map((probe, index) => (
                                <ScoopAccordion
                                    key={index}
                                    defaultExpanded={index === 0}
                                    className={styles.itemAccordion}
                                >
                                    <ScoopAccordion.Summary>
                                        <div className={styles.itemSummary}>
                                            <ScoopText className={styles.itemSummaryTitle}>
                                                {probe.name || probe.probe_id || `Probe ${index + 1}`}
                                            </ScoopText>
                                            {probe.classification ? (
                                                <ScoopText className={styles.itemSummaryMeta}>
                                                    {probe.classification}
                                                </ScoopText>
                                            ) : null}
                                        </div>
                                    </ScoopAccordion.Summary>
                                    <ScoopAccordion.Details>
                                        <div className={styles.itemCard}>
                                    <ScoopRow className={styles.itemHeader}>
                                        <FormField label="Probe ID">
                                            <ScoopTextField
                                                value={probe.probe_id}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'zo',
                                                        index,
                                                        'probe_id',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="probe_id"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                        <ScoopButton
                                            variant="grey"
                                            size="xs"
                                            onClick={() => handleRemoveProbe('zo', index)}
                                            disabled={isSaving}
                                        >
                                            <TrashIcon size={16} />
                                        </ScoopButton>
                                    </ScoopRow>
                                    <ScoopRow className={styles.fieldRow}>
                                        <FormField label="Name" helpText="Human-readable name">
                                            <ScoopTextField
                                                value={probe.name}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'zo',
                                                        index,
                                                        'name',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Entity Drill"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                        <FormField label="Classification">
                                            <ScoopTextField
                                                value={probe.classification}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'zo',
                                                        index,
                                                        'classification',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="ZERO_OVERHEAD"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                    </ScoopRow>
                                    <ScoopRow className={styles.fieldRow}>
                                        <FormField label="Drill Dimension">
                                            <ScoopTextField
                                                value={probe.drill_dimension}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'zo',
                                                        index,
                                                        'drill_dimension',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Column to GROUP BY"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                        <FormField label="Metrics">
                                            <ScoopTextField
                                                value={probe.metrics}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'zo',
                                                        index,
                                                        'metrics',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="metric1, metric2"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                    </ScoopRow>
                                    <ScoopRow className={styles.fieldRow}>
                                        <FormField label="Sort By">
                                            <ScoopTextField
                                                value={probe.sort_by}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'zo',
                                                        index,
                                                        'sort_by',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Sort column"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                        <FormField label="Inherit Parent Filter">
                                            <ScoopSwitch
                                                checked={probe.inherit_parent_filter ?? true}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'zo',
                                                        index,
                                                        'inherit_parent_filter',
                                                        e.target.checked
                                                    )
                                                }
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                    </ScoopRow>
                                    <FormField label="Purpose">
                                        <ScoopTextField
                                            value={probe.purpose}
                                            onChange={(e) =>
                                                handleProbeChange(
                                                    'zo',
                                                    index,
                                                    'purpose',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Documentation"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <ScoopRow className={styles.fieldRow}>
                                        <FormField
                                            label="Output Columns"
                                            helpText="Comma-separated"
                                        >
                                            <ScoopTextField
                                                value={probe.output_columns}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'zo',
                                                        index,
                                                        'output_columns',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Column1, Column2"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                        <FormField label="Output Type">
                                            <ScoopSelector
                                                value={probe.expected_output_type}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'zo',
                                                        index,
                                                        'expected_output_type',
                                                        e.target.value
                                                    )
                                                }
                                                options={OUTPUT_TYPE_OPTIONS}
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                    </ScoopRow>
                                    <FormField
                                        label="Dataset Requirements"
                                        helpText="JSON mapping metrics to datasets"
                                        error={jsonErrors[`zo-${index}`]}
                                    >
                                        <ScoopTextField
                                            value={probe.dataset_requirements}
                                            onChange={(e) =>
                                                handleProbeChange(
                                                    'zo',
                                                    index,
                                                    'dataset_requirements',
                                                    e.target.value
                                                )
                                            }
                                            onBlur={(e) =>
                                                handleJsonBlur(`zo-${index}`, e.target.value)
                                            }
                                            placeholder='{"metric": {"dataset": "ID", "column": "Name"}}'
                                            multiline
                                            rows={2}
                                            className={styles.codeInput}
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                        </div>
                                    </ScoopAccordion.Details>
                                </ScoopAccordion>
                            ))
                        )}
                        <ScoopButton
                            variant="grey"
                            size="sm"
                            onClick={() => handleAddProbe('zo')}
                            disabled={isSaving}
                            leftSection={<PlusIcon size={16} />}
                        >
                            Add Zero Overhead Drill
                        </ScoopButton>
                    </div>
                </ScoopTabPanel>

                <ScoopTabPanel value="ml" selectedValue={activeProbeTab}>
                    <div className={styles.itemList}>
                        {formData.ml_probes.length === 0 ? (
                            <ScoopText className={styles.emptyMessage}>
                                No ML period comparison probes defined.
                            </ScoopText>
                        ) : (
                            formData.ml_probes.map((probe, index) => (
                                <ScoopAccordion
                                    key={index}
                                    defaultExpanded={index === 0}
                                    className={styles.itemAccordion}
                                >
                                    <ScoopAccordion.Summary>
                                        <div className={styles.itemSummary}>
                                            <ScoopText className={styles.itemSummaryTitle}>
                                                {probe.name || probe.probe_id || `Probe ${index + 1}`}
                                            </ScoopText>
                                            {probe.classification ? (
                                                <ScoopText className={styles.itemSummaryMeta}>
                                                    {probe.classification}
                                                </ScoopText>
                                            ) : null}
                                        </div>
                                    </ScoopAccordion.Summary>
                                    <ScoopAccordion.Details>
                                        <div className={styles.itemCard}>
                                    <ScoopRow className={styles.itemHeader}>
                                        <FormField label="Probe ID">
                                            <ScoopTextField
                                                value={probe.probe_id}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'ml',
                                                        index,
                                                        'probe_id',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="probe_id"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                        <ScoopButton
                                            variant="grey"
                                            size="xs"
                                            onClick={() => handleRemoveProbe('ml', index)}
                                            disabled={isSaving}
                                        >
                                            <TrashIcon size={16} />
                                        </ScoopButton>
                                    </ScoopRow>
                                    <ScoopRow className={styles.fieldRow}>
                                        <FormField label="Name" helpText="Human-readable name">
                                            <ScoopTextField
                                                value={probe.name}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'ml',
                                                        index,
                                                        'name',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="ML Comparison"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                        <FormField label="Classification">
                                            <ScoopTextField
                                                value={probe.classification}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'ml',
                                                        index,
                                                        'classification',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="ML_COMPARISON"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                    </ScoopRow>
                                    <ScoopRow className={styles.fieldRow}>
                                        <FormField label="ML Attributes">
                                            <ScoopTextField
                                                value={probe.ml_attributes}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'ml',
                                                        index,
                                                        'ml_attributes',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="dimension1, dimension2"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                        <FormField label="Comparison Baseline">
                                            <ScoopSelector
                                                value={probe.comparison_baseline}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'ml',
                                                        index,
                                                        'comparison_baseline',
                                                        e.target.value
                                                    )
                                                }
                                                options={COMPARISON_BASELINE_OPTIONS}
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                    </ScoopRow>
                                    <FormField label="Purpose">
                                        <ScoopTextField
                                            value={probe.purpose}
                                            onChange={(e) =>
                                                handleProbeChange(
                                                    'ml',
                                                    index,
                                                    'purpose',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Documentation"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <ScoopRow className={styles.fieldRow}>
                                        <FormField
                                            label="Output Columns"
                                            helpText="Comma-separated"
                                        >
                                            <ScoopTextField
                                                value={probe.output_columns}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'ml',
                                                        index,
                                                        'output_columns',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Column1, Column2"
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                        <FormField label="Output Type">
                                            <ScoopSelector
                                                value={probe.expected_output_type}
                                                onChange={(e) =>
                                                    handleProbeChange(
                                                        'ml',
                                                        index,
                                                        'expected_output_type',
                                                        e.target.value
                                                    )
                                                }
                                                options={OUTPUT_TYPE_OPTIONS}
                                                disabled={isSaving}
                                            />
                                        </FormField>
                                    </ScoopRow>
                                    <FormField
                                        label="Dataset Requirements"
                                        helpText="JSON mapping metrics to datasets"
                                    >
                                        <ScoopTextField
                                            value={probe.dataset_requirements}
                                            onChange={(e) =>
                                                handleProbeChange(
                                                    'ml',
                                                    index,
                                                    'dataset_requirements',
                                                    e.target.value
                                                )
                                            }
                                            placeholder='{"metric": {"dataset": "ID", "column": "Name"}}'
                                            multiline
                                            rows={2}
                                            className={styles.codeInput}
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                        </div>
                                    </ScoopAccordion.Details>
                                </ScoopAccordion>
                            ))
                        )}
                        <ScoopButton
                            variant="grey"
                            size="sm"
                            onClick={() => handleAddProbe('ml')}
                            disabled={isSaving}
                            leftSection={<PlusIcon size={16} />}
                        >
                            Add ML Comparison Probe
                        </ScoopButton>
                    </div>
                </ScoopTabPanel>
            </div>
        </div>
    )

    const renderPatternsTab = () => (
        <div className={styles.tabContent}>
            {/* Detection Patterns */}
            <div className={styles.section}>
                <ScoopText className={styles.sectionTitle}>Detection Patterns</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    Define rules that detect business anomalies. When triggered, patterns can spawn
                    deeper investigations or complete the analysis.
                </ScoopText>
                <div className={styles.itemList}>
                    {formData.patterns.length === 0 ? (
                        <ScoopText className={styles.emptyMessage}>
                            No detection patterns defined.
                        </ScoopText>
                    ) : (
                        formData.patterns.map((pattern, index) => (
                            <div key={index} className={styles.itemCard}>
                                <ScoopRow className={styles.itemHeader}>
                                    <FormField label="Pattern Name">
                                        <ScoopTextField
                                            value={pattern.name}
                                            onChange={(e) =>
                                                handlePatternChange(index, 'name', e.target.value)
                                            }
                                            placeholder="pattern_name"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <ScoopButton
                                        variant="grey"
                                        size="xs"
                                        onClick={() => handleRemovePattern(index)}
                                        disabled={isSaving}
                                    >
                                        <TrashIcon size={16} />
                                    </ScoopButton>
                                </ScoopRow>
                                <FormField label="Description">
                                    <ScoopTextField
                                        value={pattern.description}
                                        onChange={(e) =>
                                            handlePatternChange(
                                                index,
                                                'description',
                                                e.target.value
                                            )
                                        }
                                        placeholder="What this pattern detects"
                                        disabled={isSaving}
                                    />
                                </FormField>
                                <FormField label="Detection Expression">
                                    <ScoopTextField
                                        value={pattern.expression}
                                        onChange={(e) =>
                                            handlePatternChange(index, 'expression', e.target.value)
                                        }
                                        placeholder="Excel-like formula or condition"
                                        className={styles.codeInput}
                                        disabled={isSaving}
                                    />
                                </FormField>
                                <ScoopRow className={styles.fieldRow}>
                                    <FormField label="Confidence Threshold">
                                        <ScoopTextField
                                            type="number"
                                            value={pattern.confidence_threshold}
                                            onChange={(e) =>
                                                handlePatternChange(
                                                    index,
                                                    'confidence_threshold',
                                                    parseFloat(e.target.value) || 0
                                                )
                                            }
                                            placeholder="0.5"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <FormField label="Action">
                                        <ScoopSelector
                                            value={pattern.action}
                                            onChange={(e) =>
                                                handlePatternChange(index, 'action', e.target.value)
                                            }
                                            options={ACTION_OPTIONS}
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <FormField label="Affected Metric">
                                        <ScoopTextField
                                            value={pattern.affected_metric}
                                            onChange={(e) =>
                                                handlePatternChange(
                                                    index,
                                                    'affected_metric',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="metric_name"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                </ScoopRow>
                                <ScoopRow className={styles.fieldRow}>
                                    <FormField
                                        label="Capture Triggers"
                                        helpText="Capture entity IDs matching this pattern for spawn targeting"
                                    >
                                        <ScoopSwitch
                                            checked={pattern.capture_triggers || false}
                                            onChange={(e) =>
                                                handlePatternChange(
                                                    index,
                                                    'capture_triggers',
                                                    e.target.checked
                                                )
                                            }
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <FormField
                                        label="Trigger Threshold"
                                        helpText="Threshold value for trigger activation"
                                    >
                                        <ScoopTextField
                                            type="number"
                                            value={pattern.trigger_threshold}
                                            onChange={(e) =>
                                                handlePatternChange(
                                                    index,
                                                    'trigger_threshold',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="0.5"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <FormField
                                        label="Trigger Expression"
                                        helpText="Expression to extract trigger entity IDs"
                                    >
                                        <ScoopTextField
                                            value={pattern.trigger_expression || ''}
                                            onChange={(e) =>
                                                handlePatternChange(
                                                    index,
                                                    'trigger_expression',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="GROUP_BY_COLUMN"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                </ScoopRow>
                            </div>
                        ))
                    )}
                    <ScoopButton
                        variant="grey"
                        size="sm"
                        onClick={handleAddPattern}
                        disabled={isSaving}
                        leftSection={<PlusIcon size={16} />}
                    >
                        Add Detection Pattern
                    </ScoopButton>
                </div>
            </div>

            {/* Interpretation Guidance */}
            <div className={styles.section}>
                <ScoopText className={styles.sectionTitle}>Interpretation Guidance</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    How to interpret detected patterns, including severity levels and recommended
                    actions.
                </ScoopText>
                <div className={styles.itemList}>
                    {formData.interpretation_guidance.length === 0 ? (
                        <ScoopText className={styles.emptyMessage}>
                            No interpretation guidance defined.
                        </ScoopText>
                    ) : (
                        formData.interpretation_guidance.map((interp, index) => (
                            <div key={index} className={styles.itemCard}>
                                <ScoopRow className={styles.itemHeader}>
                                    <FormField
                                        label="Pattern Name"
                                        helpText="Which pattern this guidance applies to"
                                    >
                                        <ScoopTextField
                                            value={interp.pattern_name}
                                            onChange={(e) =>
                                                handleInterpretationChange(
                                                    index,
                                                    'pattern_name',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="severe_imbalance_found"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <ScoopButton
                                        variant="grey"
                                        size="xs"
                                        onClick={() => handleRemoveInterpretation(index)}
                                        disabled={isSaving}
                                    >
                                        <TrashIcon size={16} />
                                    </ScoopButton>
                                </ScoopRow>
                                <FormField
                                    label="Severity Thresholds"
                                    helpText="JSON object defining severity levels"
                                >
                                    <ScoopTextField
                                        value={interp.severity_thresholds}
                                        onChange={(e) =>
                                            handleInterpretationChange(
                                                index,
                                                'severity_thresholds',
                                                e.target.value
                                            )
                                        }
                                        placeholder='{"critical": "> 40% spread", "severe": "> 30% spread"}'
                                        multiline
                                        rows={3}
                                        className={styles.codeInput}
                                        disabled={isSaving}
                                    />
                                </FormField>
                                <FormField
                                    label="Business Impact"
                                    helpText="What this pattern means for the business"
                                >
                                    <ScoopTextField
                                        value={interp.business_impact}
                                        onChange={(e) =>
                                            handleInterpretationChange(
                                                index,
                                                'business_impact',
                                                e.target.value
                                            )
                                        }
                                        placeholder="Large PLO-Inventory spread indicates fundamental business imbalance..."
                                        multiline
                                        rows={4}
                                        disabled={isSaving}
                                    />
                                </FormField>
                                <FormField
                                    label="Recommended Action"
                                    helpText="What action to take when pattern is detected"
                                >
                                    <ScoopTextField
                                        value={interp.recommended_action}
                                        onChange={(e) =>
                                            handleInterpretationChange(
                                                index,
                                                'recommended_action',
                                                e.target.value
                                            )
                                        }
                                        placeholder="Deep investigation of top entities by spread magnitude..."
                                        multiline
                                        rows={2}
                                        disabled={isSaving}
                                    />
                                </FormField>
                                <FormField
                                    label="Executive Framing"
                                    helpText="How to frame this pattern for executive-level reports"
                                >
                                    <ScoopTextField
                                        value={interp.executive_framing || ''}
                                        onChange={(e) =>
                                            handleInterpretationChange(
                                                index,
                                                'executive_framing',
                                                e.target.value
                                            )
                                        }
                                        placeholder="Portfolio imbalance risk requiring leadership attention..."
                                        multiline
                                        rows={2}
                                        disabled={isSaving}
                                    />
                                </FormField>
                            </div>
                        ))
                    )}
                    <ScoopButton
                        variant="grey"
                        size="sm"
                        onClick={handleAddInterpretation}
                        disabled={isSaving}
                        leftSection={<PlusIcon size={16} />}
                    >
                        Add Interpretation Guidance
                    </ScoopButton>
                </div>
            </div>
        </div>
    )

    const renderBehaviorTab = () => (
        <div className={styles.tabContent}>
            {/* Spawn Enabled Toggle */}
            <div className={styles.section}>
                <ScoopRow className={styles.fieldRow}>
                    <FormField
                        label="Spawn Enabled"
                        helpText="When disabled, this context runs screening only (QA mode) without spawning child investigations"
                    >
                        <ScoopSwitch
                            checked={formData.spawn_enabled}
                            onChange={(e) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    spawn_enabled: e.target.checked,
                                }))
                                setHasChanges(true)
                            }}
                            disabled={isSaving}
                        />
                    </FormField>
                </ScoopRow>
            </div>

            {/* Spawn Guidance */}
            <div className={styles.section}>
                <ScoopText className={styles.sectionTitle}>Spawn Guidance</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    Configure how this context spawns child investigations based on detected
                    patterns.
                </ScoopText>
                <div className={styles.itemList}>
                    {formData.spawn_guidance.length === 0 ? (
                        <ScoopText className={styles.emptyMessage}>
                            No spawn guidance defined.
                        </ScoopText>
                    ) : (
                        formData.spawn_guidance.map((spawn, index) => (
                            <div key={index} className={styles.itemCard}>
                                <ScoopRow className={styles.itemHeader}>
                                    <FormField label="Trigger Pattern">
                                        <ScoopTextField
                                            value={spawn.trigger_pattern}
                                            onChange={(e) =>
                                                handleSpawnChange(
                                                    index,
                                                    'trigger_pattern',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="pattern_name"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <ScoopButton
                                        variant="grey"
                                        size="xs"
                                        onClick={() => handleRemoveSpawn(index)}
                                        disabled={isSaving}
                                    >
                                        <TrashIcon size={16} />
                                    </ScoopButton>
                                </ScoopRow>
                                <ScoopRow className={styles.fieldRow}>
                                    <FormField label="Spawn Mode">
                                        <ScoopSelector
                                            value={spawn.spawn_mode}
                                            onChange={(e) =>
                                                handleSpawnChange(
                                                    index,
                                                    'spawn_mode',
                                                    e.target.value
                                                )
                                            }
                                            options={SPAWN_MODE_OPTIONS}
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <FormField label="Target Context">
                                        <ScoopTextField
                                            value={spawn.spawn_to_context}
                                            onChange={(e) =>
                                                handleSpawnChange(
                                                    index,
                                                    'spawn_to_context',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="target_context_name"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                </ScoopRow>
                                <ScoopRow className={styles.fieldRow}>
                                    <FormField label="Entity Dimension">
                                        <ScoopTextField
                                            value={spawn.entity_dimension}
                                            onChange={(e) =>
                                                handleSpawnChange(
                                                    index,
                                                    'entity_dimension',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Configured entity dimension"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <FormField label="Strategy">
                                        <ScoopSelector
                                            value={spawn.spawn_strategy}
                                            onChange={(e) =>
                                                handleSpawnChange(
                                                    index,
                                                    'spawn_strategy',
                                                    e.target.value
                                                )
                                            }
                                            options={STRATEGY_OPTIONS}
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                </ScoopRow>
                                <ScoopRow className={styles.fieldRow}>
                                    <FormField label="Max Spawns">
                                        <ScoopTextField
                                            type="number"
                                            value={spawn.max_spawns}
                                            onChange={(e) =>
                                                handleSpawnChange(
                                                    index,
                                                    'max_spawns',
                                                    parseInt(e.target.value) || 1
                                                )
                                            }
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <FormField
                                        label="Priority Modifier"
                                        helpText="Multiplier for spawn priority"
                                    >
                                        <ScoopTextField
                                            type="number"
                                            value={spawn.priority_modifier}
                                            onChange={(e) =>
                                                handleSpawnChange(
                                                    index,
                                                    'priority_modifier',
                                                    parseFloat(e.target.value) || 1
                                                )
                                            }
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                </ScoopRow>
                                <FormField label="Reason">
                                    <ScoopTextField
                                        value={spawn.reason}
                                        onChange={(e) =>
                                            handleSpawnChange(index, 'reason', e.target.value)
                                        }
                                        placeholder="CRITICAL: Entity with large PLO-Inv spread needs comprehensive diagnosis"
                                        multiline
                                        rows={2}
                                        disabled={isSaving}
                                    />
                                </FormField>
                            </div>
                        ))
                    )}
                    <ScoopButton
                        variant="grey"
                        size="sm"
                        onClick={handleAddSpawn}
                        disabled={isSaving}
                        leftSection={<PlusIcon size={16} />}
                    >
                        Add Spawn Guidance
                    </ScoopButton>
                </div>
            </div>

            {/* Termination Conditions */}
            <div className={styles.section}>
                <ScoopText className={styles.sectionTitle}>Termination Conditions</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    Define when this investigation should end and what action to take.
                </ScoopText>
                <div className={styles.itemList}>
                    {formData.termination_conditions.length === 0 ? (
                        <ScoopText className={styles.emptyMessage}>
                            No termination conditions defined.
                        </ScoopText>
                    ) : (
                        formData.termination_conditions.map((term, index) => (
                            <div key={index} className={styles.itemCard}>
                                <ScoopRow className={styles.itemHeader}>
                                    <FormField label="Condition">
                                        <ScoopTextField
                                            value={term.condition}
                                            onChange={(e) =>
                                                handleTerminationChange(
                                                    index,
                                                    'condition',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="no_rows_returned"
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <ScoopButton
                                        variant="grey"
                                        size="xs"
                                        onClick={() => handleRemoveTermination(index)}
                                        disabled={isSaving}
                                    >
                                        <TrashIcon size={16} />
                                    </ScoopButton>
                                </ScoopRow>
                                <ScoopRow className={styles.fieldRow}>
                                    <FormField label="Action">
                                        <ScoopSelector
                                            value={term.action}
                                            onChange={(e) =>
                                                handleTerminationChange(
                                                    index,
                                                    'action',
                                                    e.target.value
                                                )
                                            }
                                            options={TERMINATION_ACTION_OPTIONS}
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <FormField label="Severity">
                                        <ScoopSelector
                                            value={term.severity}
                                            onChange={(e) =>
                                                handleTerminationChange(
                                                    index,
                                                    'severity',
                                                    e.target.value
                                                )
                                            }
                                            options={SEVERITY_OPTIONS}
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                </ScoopRow>
                                <FormField label="Message">
                                    <ScoopTextField
                                        value={term.message}
                                        onChange={(e) =>
                                            handleTerminationChange(
                                                index,
                                                'message',
                                                e.target.value
                                            )
                                        }
                                        placeholder="Screening complete. No entities currently show severe imbalance."
                                        multiline
                                        rows={2}
                                        disabled={isSaving}
                                    />
                                </FormField>
                            </div>
                        ))
                    )}
                    <ScoopButton
                        variant="grey"
                        size="sm"
                        onClick={handleAddTermination}
                        disabled={isSaving}
                        leftSection={<PlusIcon size={16} />}
                    >
                        Add Termination Condition
                    </ScoopButton>
                </div>
            </div>
        </div>
    )

    const renderBusinessTab = () => (
        <div className={styles.tabContent}>
            {/* Business Context */}
            <div className={styles.section}>
                <ScoopText className={styles.sectionTitle}>Business Context</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    Business ownership, operational context, and KPI targets.
                </ScoopText>
                <ScoopColumn className={styles.fields}>
                    <FormField
                        label="Methodology"
                        helpText="Analytical methodology description included in DOCX investigation reports"
                    >
                        <ScoopTextField
                            value={formData.business_context.methodology}
                            onChange={(e) =>
                                handleBusinessContextChange('methodology', e.target.value)
                            }
                            placeholder="Describe the analytical methodology used in this investigation context..."
                            multiline
                            rows={4}
                            disabled={isSaving}
                        />
                    </FormField>
                    <ScoopRow className={styles.fieldRow}>
                        <FormField label="Owner" helpText="Who owns this context">
                            <ScoopTextField
                                value={formData.business_context.owner}
                                onChange={(e) =>
                                    handleBusinessContextChange('owner', e.target.value)
                                }
                                placeholder="CFO / Operations Leadership"
                                disabled={isSaving}
                            />
                        </FormField>
                        <FormField label="Frequency" helpText="How often to run">
                            <ScoopTextField
                                value={formData.business_context.frequency}
                                onChange={(e) =>
                                    handleBusinessContextChange('frequency', e.target.value)
                                }
                                placeholder="Monthly or on-demand"
                                disabled={isSaving}
                            />
                        </FormField>
                    </ScoopRow>
                    <FormField label="Escalation" helpText="When and how to escalate">
                        <ScoopTextField
                            value={formData.business_context.escalation}
                            onChange={(e) =>
                                handleBusinessContextChange('escalation', e.target.value)
                            }
                            placeholder="Entities with >40% spread AND declining revenue require leadership review"
                            multiline
                            rows={2}
                            disabled={isSaving}
                        />
                    </FormField>

                    <ScoopText className={styles.subsectionTitle}>KPI Targets</ScoopText>
                    <ScoopRow className={styles.fieldRow}>
                        <FormField label="Goal">
                            <ScoopTextField
                                value={formData.business_context.kpi_targets.goal}
                                onChange={(e) => handleKpiTargetsChange('goal', e.target.value)}
                                placeholder="Zero entities with >30% spread and negative revenue"
                                disabled={isSaving}
                            />
                        </FormField>
                        <FormField label="Acceptable">
                            <ScoopTextField
                                value={formData.business_context.kpi_targets.acceptable}
                                onChange={(e) =>
                                    handleKpiTargetsChange('acceptable', e.target.value)
                                }
                                placeholder="< 3 entities in severe category"
                                disabled={isSaving}
                            />
                        </FormField>
                    </ScoopRow>
                    <FormField label="Action Required">
                        <ScoopTextField
                            value={formData.business_context.kpi_targets.action_required}
                            onChange={(e) =>
                                handleKpiTargetsChange('action_required', e.target.value)
                            }
                            placeholder="> 5 entities with >30% spread or any with >50% spread"
                            disabled={isSaving}
                        />
                    </FormField>

                    <ScoopText className={styles.subsectionTitle}>Fiscal Calendar</ScoopText>
                    <ScoopRow className={styles.fieldRow}>
                        <FormField label="Q1">
                            <ScoopTextField
                                value={formData.business_context.fiscal_calendar.Q1}
                                onChange={(e) => handleFiscalCalendarChange('Q1', e.target.value)}
                                placeholder="First fiscal quarter date range"
                                disabled={isSaving}
                            />
                        </FormField>
                        <FormField label="Q2">
                            <ScoopTextField
                                value={formData.business_context.fiscal_calendar.Q2}
                                onChange={(e) => handleFiscalCalendarChange('Q2', e.target.value)}
                                placeholder="Second fiscal quarter date range"
                                disabled={isSaving}
                            />
                        </FormField>
                    </ScoopRow>
                    <ScoopRow className={styles.fieldRow}>
                        <FormField label="Q3">
                            <ScoopTextField
                                value={formData.business_context.fiscal_calendar.Q3}
                                onChange={(e) => handleFiscalCalendarChange('Q3', e.target.value)}
                                placeholder="Third fiscal quarter date range"
                                disabled={isSaving}
                            />
                        </FormField>
                        <FormField label="Q4">
                            <ScoopTextField
                                value={formData.business_context.fiscal_calendar.Q4}
                                onChange={(e) => handleFiscalCalendarChange('Q4', e.target.value)}
                                placeholder="Fourth fiscal quarter date range"
                                disabled={isSaving}
                            />
                        </FormField>
                    </ScoopRow>
                    <FormField label="Fiscal Calendar Note">
                        <ScoopTextField
                            value={formData.business_context.fiscal_calendar.note}
                            onChange={(e) => handleFiscalCalendarChange('note', e.target.value)}
                            placeholder="e.g. Fiscal year ends September 30"
                            disabled={isSaving}
                        />
                    </FormField>
                </ScoopColumn>
            </div>

            {/* Synthesis Options — investigation.synthesis_options (audit F12) */}
            <div className={styles.section}>
                <ScoopText className={styles.sectionTitle}>Synthesis Options</ScoopText>
                <ScoopText className={styles.sectionDescription}>
                    ML synthesis settings for this context (stored at
                    investigation.synthesis_options).
                </ScoopText>
                <ScoopColumn className={styles.fields}>
                    <ScoopRow className={styles.fieldRow}>
                        <FormField label="ML Enabled" helpText="Run the ML synthesis pass">
                            <ScoopSwitch
                                checked={formData.synthesis_options.ml_enabled === true}
                                onChange={(e) =>
                                    handleSynthesisChange('ml_enabled', e.target.checked)
                                }
                                disabled={isSaving}
                            />
                        </FormField>
                        <FormField label="ML Threshold" helpText="Confidence threshold for ML">
                            <ScoopTextField
                                type="number"
                                value={formData.synthesis_options.ml_threshold}
                                onChange={(e) =>
                                    handleSynthesisChange('ml_threshold', e.target.value)
                                }
                                placeholder="0.7"
                                disabled={isSaving}
                            />
                        </FormField>
                        <FormField label="ML Timeout" helpText="Seconds before ML gives up">
                            <ScoopTextField
                                type="number"
                                value={formData.synthesis_options.ml_timeout}
                                onChange={(e) =>
                                    handleSynthesisChange('ml_timeout', e.target.value)
                                }
                                placeholder="120"
                                disabled={isSaving}
                            />
                        </FormField>
                        <FormField label="Max Queries" helpText="Query budget for synthesis">
                            <ScoopTextField
                                type="number"
                                value={formData.synthesis_options.max_queries}
                                onChange={(e) =>
                                    handleSynthesisChange('max_queries', e.target.value)
                                }
                                placeholder="10"
                                disabled={isSaving}
                            />
                        </FormField>
                    </ScoopRow>
                    <FormField label="ML Query" helpText="Query the ML synthesis pass runs">
                        <ScoopTextField
                            value={formData.synthesis_options.ml_query}
                            onChange={(e) => handleSynthesisChange('ml_query', e.target.value)}
                            placeholder="e.g. compare declining entities against stable peers"
                            multiline
                            rows={2}
                            disabled={isSaving}
                        />
                    </FormField>
                    <FormField
                        label="Narrative Voice"
                        helpText="Voice/tone instruction for the synthesis narrative"
                    >
                        <ScoopTextField
                            value={formData.synthesis_options.narrative_voice}
                            onChange={(e) =>
                                handleSynthesisChange('narrative_voice', e.target.value)
                            }
                            placeholder="Operator-facing, factual, no drama"
                            disabled={isSaving}
                        />
                    </FormField>
                    <FormField
                        label="Internal Guidance"
                        helpText="One guidance rule per line — injected into the synthesis prompt"
                    >
                        <ScoopTextField
                            value={formData.synthesis_options.internal_guidance}
                            onChange={(e) =>
                                handleSynthesisChange('internal_guidance', e.target.value)
                            }
                            placeholder={'PLO lags NLM by ~6 months\nNever cite positive PLO as health evidence when NLM declining'}
                            multiline
                            rows={4}
                            disabled={isSaving}
                        />
                    </FormField>
                </ScoopColumn>
            </div>
        </div>
    )

    // =========================================================================
    // RENDER
    // =========================================================================

    if (isLoading) {
        return (
            <ScoopCenter className={styles.loading}>
                <ScoopSpinner size={32} />
                <ScoopText>Loading context...</ScoopText>
            </ScoopCenter>
        )
    }

    const displayName = formData.metadata.display_name || contextName || 'Context'

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <ScoopText as="h2" className={styles.title}>
                        {displayName}
                    </ScoopText>
                    <ScoopText className={styles.description}>
                        Configure this investigation context's metadata, probes, patterns, and
                        behavior.
                    </ScoopText>
                </div>
                <ScoopRow className={styles.actions}>
                    {hasChanges && (
                        <ScoopButton
                            variant="grey"
                            size="sm"
                            onClick={() => setShowResetConfirm(true)}
                            disabled={isSaving}
                            leftSection={<ArrowCounterClockwiseIcon size={16} />}
                        >
                            Reset
                        </ScoopButton>
                    )}
                    <ScoopButton
                        variant="default"
                        size="sm"
                        onClick={handleSave}
                        disabled={!hasChanges || isSaving}
                        loading={isSaving}
                        leftSection={<FloppyDiskIcon size={16} />}
                    >
                        Save Changes
                    </ScoopButton>
                </ScoopRow>
            </div>

            <div className={styles.tabContainer}>
                <ScoopTab
                    tabs={tabsWithDirty}
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant="underline"
                    fullWidth={false}
                />
            </div>

            <div className={styles.content}>
                <ScoopTabPanel value="overview" selectedValue={activeTab}>
                    {renderOverviewTab()}
                </ScoopTabPanel>
                <ScoopTabPanel value="validation" selectedValue={activeTab}>
                    {renderValidationTab()}
                </ScoopTabPanel>
                <ScoopTabPanel value="definitions" selectedValue={activeTab}>
                    {renderDefinitionsTab()}
                </ScoopTabPanel>
                <ScoopTabPanel value="probes" selectedValue={activeTab}>
                    {renderProbesTab()}
                </ScoopTabPanel>
                <ScoopTabPanel value="patterns" selectedValue={activeTab}>
                    {renderPatternsTab()}
                </ScoopTabPanel>
                <ScoopTabPanel value="behavior" selectedValue={activeTab}>
                    {renderBehaviorTab()}
                </ScoopTabPanel>
                <ScoopTabPanel value="business" selectedValue={activeTab}>
                    {renderBusinessTab()}
                </ScoopTabPanel>
            </div>

            {/* Reset discards the whole document's change-set (D26) — confirm
                and say how many tabs are touched. */}
            <ScoopDialog
                open={showResetConfirm}
                onClose={() => setShowResetConfirm(false)}
                title="Discard changes"
                maxWidth="sm"
                actions={
                    <>
                        <ScoopButton variant="grey" onClick={() => setShowResetConfirm(false)}>
                            Keep Editing
                        </ScoopButton>
                        <ScoopButton variant="danger" onClick={handleReset}>
                            Discard Changes
                        </ScoopButton>
                    </>
                }
            >
                <ScoopText>
                    This discards all unsaved changes
                    {dirtyTabs.size > 0
                        ? ` across ${dirtyTabs.size} ${dirtyTabs.size === 1 ? 'tab' : 'tabs'}`
                        : ''}{' '}
                    and restores the last saved version.
                </ScoopText>
            </ScoopDialog>
        </div>
    )
}
